import {
  getTelemetryStreamUrl,
  getTelemetryHealthUrl,
  getTelemetryVisitorsUrl,
} from './telemetryUrl';
import { formatLocationLabel } from './formatLocation';

/**
 * Client-side telemetry singleton.
 * Mirrors adventureMusicEngine: subscribe() + getState(), SSE transport.
 * Events are connection/status only — no fabricated activity lines.
 * Visitor registration is additive and does not alter metrics handling.
 */

var HISTORY_LIMIT = 60;
var MAX_EVENTS = 25;
var MAX_LOGS = 80;
var RECONNECT_MS = 2500;
var VISITOR_SESSION_KEY = 'wesam_telemetry_visitor_session';

function emptyMetrics() {
  return {
    timestamp: null,
    cpu: 0,
    memory: 0,
    heapUsed: 0,
    latency: null,
    latencyLast: null,
    requestsPerMinute: 0,
    uptime: 0,
    heartbeat: false,
    sseClients: 0,
    emailConfigured: false,
    chatbotConfigured: false,
    node: null,
    pid: null,
  };
}

function TelemetryClient() {
  this.listeners = new Set();
  this.source = null;
  this.reconnectTimer = null;
  this.started = false;
  this.visitPingSent = false;
  this.visitorRegistered = false;
  this._visitorListeners = new Set();
  this.pulseSeq = 0;
  this.toastSeq = 0;
  this.prevRpm = null;
  this.state = {
    connected: false,
    connecting: false,
    lastUpdate: null,
    metrics: emptyMetrics(),
    history: {
      cpu: [],
      memory: [],
      latency: [],
      requestsPerMinute: [],
    },
    events: [],
    logs: [],
    toasts: [],
    pulseSeq: 0,
  };
}

TelemetryClient.prototype.getState = function () {
  return this.state;
};

TelemetryClient.prototype.subscribe = function (listener) {
  var self = this;
  this.listeners.add(listener);
  listener(this.state);
  this.start();
  return function unsubscribe() {
    self.listeners.delete(listener);
    if (self.listeners.size === 0) {
      self.stop();
    }
  };
};

TelemetryClient.prototype._emit = function () {
  var snapshot = this.state;
  this.listeners.forEach(function (listener) {
    try {
      listener(snapshot);
    } catch (err) {
      // ignore subscriber errors
    }
  });
};

TelemetryClient.prototype._pushHistory = function (metrics) {
  var pairs = [
    ['cpu', metrics.cpu],
    ['memory', metrics.memory],
    ['latency', metrics.latency == null ? 0 : metrics.latency],
    ['requestsPerMinute', metrics.requestsPerMinute],
  ];
  for (var i = 0; i < pairs.length; i++) {
    var key = pairs[i][0];
    var value = pairs[i][1];
    var series = this.state.history[key].slice();
    series.push({ t: Date.now(), v: value });
    if (series.length > HISTORY_LIMIT) {
      series = series.slice(series.length - HISTORY_LIMIT);
    }
    this.state.history[key] = series;
  }
};

TelemetryClient.prototype._pushEvent = function (message, kind) {
  var entry = {
    id: 'evt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
    message: message,
    kind: kind || 'info',
    at: Date.now(),
  };
  var events = [entry].concat(this.state.events);
  if (events.length > MAX_EVENTS) {
    events = events.slice(0, MAX_EVENTS);
  }
  this.state.events = events;

  var logs = [
    {
      id: 'log-' + entry.id,
      message: message,
      at: entry.at,
    },
  ].concat(this.state.logs);
  if (logs.length > MAX_LOGS) {
    logs = logs.slice(0, MAX_LOGS);
  }
  this.state.logs = logs;
};

TelemetryClient.prototype._pushToast = function (message) {
  this.toastSeq += 1;
  var toast = {
    id: 'toast-' + this.toastSeq,
    message: message,
    at: Date.now(),
  };
  this.state.toasts = this.state.toasts.concat([toast]).slice(-4);
};

TelemetryClient.prototype.dismissToast = function (id) {
  this.state.toasts = this.state.toasts.filter(function (t) {
    return t.id !== id;
  });
  this._emit();
};

TelemetryClient.prototype.clearLogs = function () {
  this.state.logs = [];
  this._emit();
};

/** Additive console line (visitor analytics, etc.) — does not change metrics. */
TelemetryClient.prototype.pushLog = function (message, kind) {
  this._pushEvent(message, kind || 'info');
  this._emit();
};

/**
 * Subscribe to additive named SSE visitor events (does not affect metrics).
 */
TelemetryClient.prototype.subscribeVisitorEvents = function (listener) {
  var self = this;
  if (!this._visitorListeners) {
    this._visitorListeners = new Set();
  }
  this._visitorListeners.add(listener);
  return function unsubscribe() {
    self._visitorListeners.delete(listener);
  };
};

TelemetryClient.prototype._getVisitorSessionId = function () {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return 'anon-' + Date.now().toString(36);
  }
  try {
    var existing = window.sessionStorage.getItem(VISITOR_SESSION_KEY);
    if (existing) return existing;
    var id =
      'vs-' +
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2, 10);
    window.sessionStorage.setItem(VISITOR_SESSION_KEY, id);
    return id;
  } catch (err) {
    return 'anon-' + Date.now().toString(36);
  }
};

/**
 * Additive once-per-session visitor registration.
 * Separate from health ping so /health response stays unchanged.
 */
TelemetryClient.prototype._registerVisitor = function () {
  if (this.visitorRegistered || typeof fetch === 'undefined') return;
  this.visitorRegistered = true;
  var self = this;
  var url = getTelemetryVisitorsUrl();
  var payload = {
    sessionId: this._getVisitorSessionId(),
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    timezone: '',
  };
  try {
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      payload.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  } catch (err) {
    // timezone optional
  }

  self._pushEvent('Visitor detected', 'visitor');
  self._emit();

  fetch(url, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then(function (res) {
      if (!res.ok) return null;
      return res.json();
    })
    .then(function (data) {
      if (!data || !data.ok) return;
      var loc = data.location || {};
      if (loc.isLocal) {
        self._pushEvent(
          'Local visit' + (loc.city ? ' · ' + loc.city : ''),
          'visitor'
        );
      } else {
        var place = formatLocationLabel(loc);
        if (place) {
          self._pushEvent('Location resolved: ' + place, 'visitor');
        }
        self._pushEvent('Location counter updated', 'visitor');
      }
      self._emit();
    })
    .catch(function () {
      // Visitor registration is best-effort
    });
};

TelemetryClient.prototype._onPayload = function (metrics) {
  var becameLive = !this.state.connected;
  var prev = this.state.metrics;
  this.pulseSeq += 1;
  this.state.connected = true;
  this.state.connecting = false;
  this.state.lastUpdate = Date.now();
  this.state.metrics = metrics;
  this.state.pulseSeq = this.pulseSeq;
  this._pushHistory(metrics);

  if (
    this.prevRpm !== null &&
    metrics.requestsPerMinute > this.prevRpm
  ) {
    this._pushEvent(
      'API traffic · ' + metrics.requestsPerMinute + ' req in last 60s',
      'traffic'
    );
  }
  this.prevRpm = metrics.requestsPerMinute;

  if (
    prev &&
    (prev.emailConfigured !== metrics.emailConfigured ||
      prev.chatbotConfigured !== metrics.chatbotConfigured)
  ) {
    this._pushEvent(
      'Integrations · email ' +
        (metrics.emailConfigured ? 'configured' : 'not configured') +
        ', chatbot ' +
        (metrics.chatbotConfigured ? 'configured' : 'not configured'),
      'config'
    );
  }

  if (becameLive) {
    var hadDisconnect = this.state.logs.some(function (l) {
      return l.message === 'Disconnected';
    });
    if (hadDisconnect) {
      this._pushToast('Reconnected');
      this._pushEvent('Reconnected to telemetry', 'status');
    }
  }

  this._emit();
};

TelemetryClient.prototype._pingVisit = function () {
  if (this.visitPingSent || typeof fetch === 'undefined') return;
  this.visitPingSent = true;
  var self = this;
  var url = getTelemetryHealthUrl();
  fetch(url, { method: 'GET', mode: 'cors', cache: 'no-store' })
    .then(function (res) {
      if (!res.ok) return;
      self._pushEvent('Visit ping · API health OK', 'traffic');
      self._emit();
    })
    .catch(function () {
      // Visit ping is best-effort; stream may still connect
    });
};

TelemetryClient.prototype._scheduleReconnect = function () {
  var self = this;
  if (this.reconnectTimer || !this.started) return;
  this.reconnectTimer = setTimeout(function () {
    self.reconnectTimer = null;
    if (self.started && self.listeners.size > 0) {
      self._connect();
    }
  }, RECONNECT_MS);
};

TelemetryClient.prototype._connect = function () {
  var self = this;
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
    return;
  }
  if (this.source) {
    try {
      this.source.close();
    } catch (err) {
      // ignore
    }
    this.source = null;
  }

  this.state.connecting = true;
  this.state.connected = false;
  this._emit();

  var url = getTelemetryStreamUrl();
  var source = new EventSource(url);
  this.source = source;

  source.onopen = function () {
    var firstConnect = self.state.logs.length === 0;
    self.state.connecting = false;
    self.state.connected = true;
    if (firstConnect) {
      self._pushToast('Connected');
      self._pushEvent('Connected to telemetry', 'status');
    }
    self._emit();
  };

  source.onmessage = function (event) {
    try {
      var payload = JSON.parse(event.data);
      self._onPayload(payload);
    } catch (err) {
      // ignore malformed frames
    }
  };

  // Named visitor events — ignored by onmessage; additive only
  source.addEventListener('visitor_location_update', function (event) {
    try {
      var data = JSON.parse(event.data);
      if (self._visitorListeners) {
        self._visitorListeners.forEach(function (listener) {
          try {
            listener(data);
          } catch (err) {
            // ignore
          }
        });
      }
    } catch (err) {
      // ignore malformed visitor frames
    }
  });

  source.onerror = function () {
    var wasUp = self.state.connected;
    self.state.connected = false;
    self.state.connecting = false;
    if (wasUp) {
      self._pushEvent('Disconnected', 'status');
    }
    self._emit();
    try {
      source.close();
    } catch (err) {
      // ignore
    }
    if (self.source === source) {
      self.source = null;
    }
    self._scheduleReconnect();
  };
};

TelemetryClient.prototype.start = function () {
  if (this.started) return;
  this.started = true;
  this._pingVisit();
  this._registerVisitor();
  this._connect();
};

TelemetryClient.prototype.stop = function () {
  this.started = false;
  if (this.reconnectTimer) {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
  if (this.source) {
    try {
      this.source.close();
    } catch (err) {
      // ignore
    }
    this.source = null;
  }
  this.state.connected = false;
  this.state.connecting = false;
  this._emit();
};

var singleton = null;

export function getTelemetryClient() {
  if (!singleton) {
    singleton = new TelemetryClient();
  }
  return singleton;
}
